import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chatApi,
  type ChatListItem,
  type ChatMessage,
  type ChatScope,
} from '../../api/chat';
import { getSessionUser } from '../../lib/api';
import { haptic, hapticImpact, hapticSelection } from '../../lib/telegram';
import { ErrorState, Loader } from '../../components/States';
import './ChatDesk.css';

const SCOPES: Array<{ id: ChatScope; label: string }> = [
  { id: 'all', label: 'Все' },
  { id: 'support', label: 'Поддержка' },
  { id: 'order', label: 'Заказы' },
  { id: 'complaint', label: 'Жалобы' },
];

interface ChatDeskProps {
  mode: 'user' | 'admin';
  focusOrderId?: string | null;
  focusChatId?: string | null;
  onFocusConsumed?: () => void;
}

export function ChatDesk({ mode, focusOrderId, focusChatId, onFocusConsumed }: ChatDeskProps) {
  const [scope, setScope] = useState<ChatScope>('all');
  const [items, setItems] = useState<ChatListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [active, setActive] = useState<ChatListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadGen = useRef(0);

  const load = useCallback(async () => {
    const gen = ++loadGen.current;
    const response = await chatApi.list(mode === 'admin' ? scope : 'all');
    if (gen !== loadGen.current) return;
    setItems(response.items);
    setNextCursor(response.nextCursor);
  }, [mode, scope]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);

    load()
      .catch((cause: unknown) => {
        if (!cancel) setError(cause instanceof Error ? cause.message : 'Не удалось загрузить чаты');
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });

    return () => {
      cancel = true;
    };
  }, [load]);

  useEffect(() => {
    if (active) return undefined;

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void load().catch(() => undefined);
    }, 8_000);

    return () => window.clearInterval(timer);
  }, [active, load]);

  const focusToken = focusOrderId ?? focusChatId ?? '';
  const consumedRef = useRef(onFocusConsumed);
  consumedRef.current = onFocusConsumed;

  useEffect(() => {
    if (!focusToken) return undefined;

    let ignore = false;
    const orderId = focusOrderId;
    const chatId = focusChatId;
    consumedRef.current?.();

    const open = async (): Promise<void> => {
      try {
        const chat = orderId ? await chatApi.openOrder(orderId) : await chatApi.get(chatId ?? '');
        if (!ignore) setActive(chat);
      } catch (cause) {
        if (!ignore) setError(cause instanceof Error ? cause.message : 'Не удалось открыть чат');
      }
    };

    void open();

    return () => {
      ignore = false;
    };
  }, [focusToken, focusOrderId, focusChatId]);

  const more = async (): Promise<void> => {
    if (!nextCursor) return;

    const response = await chatApi.list(mode === 'admin' ? scope : 'all', nextCursor);
    setItems((current) => [...current, ...response.items]);
    setNextCursor(response.nextCursor);
  };

  if (active) {
    return (
      <div className={`chat-desk chat-desk--${mode === 'admin' ? 'embedded' : 'page'}`}>
        <Thread
          chat={active}
          onBack={() => {
            setActive(null);
            void load().catch(() => undefined);
          }}
          onChatChange={setActive}
        />
      </div>
    );
  }

  return (
    <div className={`chat-desk chat-desk--${mode === 'admin' ? 'embedded' : 'page'}`}>
      <header className="chat-desk__head">
        <h1 className="page-title">{mode === 'admin' ? 'Чаты' : 'Чат'}</h1>
      </header>

      {mode === 'admin' ? (
        <div className="chat-desk__filters" role="tablist">
          {SCOPES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`chip${scope === item.id ? ' chip--active' : ''}`}
              onClick={() => setScope(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? <Loader /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}

      {!loading && !error ? (
        <ChatList
          items={items}
          pinSupport={mode === 'user'}
          nextCursor={nextCursor}
          onOpen={setActive}
          onMore={() => void more()}
          onDelete={async (rows) => {
            loadGen.current += 1;
            const results = await Promise.allSettled(rows.map((row) => chatApi.remove(row.id)));
            const removed = rows.filter((_, index) => results[index]?.status === 'fulfilled').map((row) => row.id);
            const gone = new Set(removed);
            setItems((current) => current.filter((row) => !gone.has(row.id)));
            return removed;
          }}
          onRestoreSupport={
            mode === 'user'
              ? async () => {
                  const item = await chatApi.openSupport();
                  setItems((current) => [item, ...current.filter((row) => row.id !== item.id)]);
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}

function ChatList({
  items,
  pinSupport,
  nextCursor,
  onOpen,
  onMore,
  onDelete,
  onRestoreSupport,
}: {
  items: ChatListItem[];
  pinSupport: boolean;
  nextCursor?: string;
  onOpen: (item: ChatListItem) => void;
  onMore: () => void;
  onDelete: (items: ChatListItem[]) => Promise<string[]>;
  onRestoreSupport?: () => Promise<void>;
}) {
  const pinned = pinSupport ? items.find((item) => item.kind === 'SUPPORT' && item.title === 'Поддержка') : undefined;
  const rest = pinned ? items.filter((item) => item.id !== pinned.id) : items;
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const selecting = selected !== null;

  const toggle = (id: string): void => {
    const item = items.find((row) => row.id === id);
    if (!item || item.kind === 'SUPPORT') return;

    setSelected((current) => {
      const next = new Set(current ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next.size === 0 ? null : next;
    });
    hapticSelection();
  };

  const removeSelected = async (): Promise<void> => {
    if (!selected || selected.size === 0 || deleting) return;

    const rows = items.filter((item) => selected.has(item.id) && item.kind !== 'SUPPORT');
    setDeleting(true);
    setDeleteError(null);
    hapticImpact('rigid');

    try {
      const removed = new Set(await onDelete(rows));
      setSelected((current) => {
        if (!current) return null;
        const next = new Set([...current].filter((id) => !removed.has(id)));
        return next.size === 0 ? null : next;
      });

      if (removed.size < rows.length) {
        setDeleteError('Не все чаты удалось удалить');
        haptic('error');
      } else {
        haptic('success');
      }
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : 'Не удалось удалить чат');
      haptic('error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {deleteError ? <p className="chat-thread__error">{deleteError}</p> : null}
      {!pinned && onRestoreSupport ? (
        <button type="button" className="chat-restore" onClick={() => void onRestoreSupport()}>
          Написать в поддержку
        </button>
      ) : null}
      {pinned ? (
        <div className="chat-pin">
          <ChatRow
            item={pinned}
            pinned
            locked
            selecting={false}
            selected={false}
            onLongPress={() => undefined}
            onOpen={() => onOpen(pinned)}
          />
        </div>
      ) : null}
      <div className="chat-list">
        {rest.length === 0 ? (
          <p className="chat-thread__note">{pinned ? 'Чаты по заказам появятся после оплаты' : 'Пока нет диалогов'}</p>
        ) : null}
        {rest.map((item) => {
          const locked = item.kind === 'SUPPORT';

          return (
            <ChatRow
              key={item.id}
              item={item}
              locked={locked}
              selecting={selecting && !locked}
              selected={!locked && (selected?.has(item.id) ?? false)}
              onLongPress={() => {
                if (locked) return;
                hapticImpact('medium');
                setSelected(new Set([item.id]));
              }}
              onOpen={() => (selecting && !locked ? toggle(item.id) : onOpen(item))}
            />
          );
        })}
        {nextCursor ? (
          <button type="button" className="chat-older" onClick={onMore}>
            Ещё
          </button>
        ) : null}
      </div>
      {selecting ? (
        <div className="chat-select-bar">
          <button type="button" className="chat-select-bar__cancel" onClick={() => setSelected(null)}>
            Отмена
          </button>
          <span className="chat-select-bar__count">Выбрано {selected?.size ?? 0}</span>
          <button
            type="button"
            className="chat-select-bar__delete"
            disabled={deleting || (selected?.size ?? 0) === 0}
            onClick={() => void removeSelected()}
          >
            {deleting ? 'Удаляю…' : 'Удалить'}
          </button>
        </div>
      ) : null}
    </>
  );
}

const HOLD_MS = 480;

function ChatRow({
  item,
  pinned,
  locked,
  selecting,
  selected,
  onLongPress,
  onOpen,
}: {
  item: ChatListItem;
  pinned?: boolean;
  locked?: boolean;
  selecting: boolean;
  selected: boolean;
  onLongPress: () => void;
  onOpen: () => void;
}) {
  const timer = useRef<number>();
  const holdFired = useRef(false);
  const movedAway = useRef(false);
  const startedAt = useRef(0);
  const origin = useRef({ x: 0, y: 0 });

  const cancelHold = (): void => {
    window.clearTimeout(timer.current);
  };

  const armSelection = (): void => {
    if (holdFired.current || selecting || locked) return;
    holdFired.current = true;
    cancelHold();
    onLongPress();
  };

  return (
    <button
      type="button"
      className={`chat-row${pinned ? ' chat-row--pinned' : ''}${selected ? ' chat-row--selected' : ''}`}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        if (event.button !== 0 || selecting || locked) return;
        holdFired.current = false;
        movedAway.current = false;
        startedAt.current = Date.now();
        origin.current = { x: event.clientX, y: event.clientY };
        cancelHold();
        timer.current = window.setTimeout(armSelection, HOLD_MS);
      }}
      onPointerMove={(event) => {
        const dx = event.clientX - origin.current.x;
        const dy = event.clientY - origin.current.y;
        if (dx * dx + dy * dy > 64) {
          movedAway.current = true;
          cancelHold();
        }
      }}
      onPointerUp={cancelHold}
      onPointerCancel={() => {
        const heldLongEnough = Date.now() - startedAt.current >= 280;
        if (!movedAway.current && heldLongEnough) armSelection();
        else cancelHold();
      }}
      onClick={() => {
        if (holdFired.current) {
          holdFired.current = false;
          return;
        }
        onOpen();
      }}
    >
      {selecting ? <span className={`chat-row__check${selected ? ' chat-row__check--on' : ''}`} aria-hidden="true" /> : null}
      <span className="chat-row__body">
        <p className="chat-row__title">{item.title}</p>
        <p className="chat-row__preview">{item.preview ?? (pinned ? 'Написать в поддержку' : 'Нет сообщений')}</p>
      </span>
      {item.complaintStatus === 'OPEN' ? <span className="chat-row__flag">жалоба</span> : null}
      {item.unread && !selecting ? <span className="chat-row__dot" aria-label="непрочитано" /> : null}
    </button>
  );
}

function Thread({
  chat,
  onBack,
  onChatChange,
}: {
  chat: ChatListItem;
  onBack: () => void;
  onChatChange: (chat: ChatListItem) => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | undefined>();
  const [text, setText] = useState('');
  const [file, setFile] = useState<{ name: string; dataUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [reason, setReason] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const fileRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const isAdmin = getSessionUser()?.role === 'ADMIN';
  messagesRef.current = messages;

  const scrollDown = useCallback(() => {
    const node = logRef.current;
    if (node && stickRef.current) node.scrollTop = node.scrollHeight;
  }, []);

  useEffect(() => {
    let cancel = false;

    chatApi
      .messages(chat.id)
      .then(async (response) => {
        if (cancel) return;
        setMessages(response.items);
        setOlderCursor(response.olderCursor);
        await chatApi.read(chat.id).catch(() => undefined);
      })
      .catch((cause: unknown) => {
        if (!cancel) setError(cause instanceof Error ? cause.message : 'Не удалось открыть переписку');
      });

    return () => {
      cancel = true;
    };
  }, [chat.id]);

  useEffect(() => {
    scrollDown();
  }, [messages, scrollDown]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;

      const lastId = messagesRef.current[messagesRef.current.length - 1]?.id;

      void chatApi
        .messages(chat.id, lastId ? { after: lastId } : undefined)
        .then(async (response) => {
          if (response.items.length === 0) return;

          setMessages((current) => {
            const known = new Set(current.map((item) => item.id));
            const fresh = response.items.filter((item) => !known.has(item.id));
            return fresh.length > 0 ? [...current, ...fresh] : current;
          });
          await chatApi.read(chat.id).catch(() => undefined);
        })
        .catch(() => undefined);
    }, 3_000);

    return () => window.clearInterval(timer);
  }, [chat.id]);

  const loadOlder = async (): Promise<void> => {
    if (!olderCursor) return;
    stickRef.current = false;
    const response = await chatApi.messages(chat.id, { before: olderCursor });
    setOlderCursor(response.olderCursor);
    setMessages((current) => [...response.items, ...current]);
  };

  const send = async (): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed && !file) return;

    setBusy(true);
    setError(null);

    try {
      const message = await chatApi.send(chat.id, {
        ...(trimmed ? { text: trimmed } : {}),
        ...(file ? { dataUrl: file.dataUrl, filename: file.name } : {}),
      });
      setMessages((current) => [...current, message]);
      setText('');
      setFile(null);
      stickRef.current = true;
      haptic('success');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не отправилось');
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (list: FileList | null): Promise<void> => {
    const next = list?.[0];
    if (!next) return;

    if (next.size > 6 * 1024 * 1024) {
      setError('Файл тяжелее 6 МБ');
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
      reader.readAsDataURL(next);
    });

    setFile({ name: next.name, dataUrl });
    setError(null);
  };

  const complain = async (): Promise<void> => {
    setBusy(true);
    setError(null);

    try {
      const updated = await chatApi.complain(chat.id, reason.trim());
      onChatChange(updated);
      setComplaintOpen(false);
      setReason('');
      const response = await chatApi.messages(chat.id);
      setMessages(response.items);
      setOlderCursor(response.olderCursor);
      haptic('success');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось отправить жалобу');
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (): Promise<void> => {
    setBusy(true);

    try {
      const updated = await chatApi.resolve(chat.id);
      onChatChange(updated);
      const response = await chatApi.messages(chat.id);
      setMessages(response.items);
      haptic('success');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось закрыть жалобу');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chat-thread">
      <header className="chat-thread__bar">
        <button type="button" className="chat-thread__back" onClick={onBack}>
          ←
        </button>
        <h2 className="chat-thread__name">{chat.title || 'Чат'}</h2>
        {chat.kind === 'ORDER' && !chat.purged && chat.complaintStatus !== 'OPEN' ? (
          <button type="button" className="chat-thread__side" onClick={() => setComplaintOpen((value) => !value)}>
            Жалоба
          </button>
        ) : null}
        {isAdmin && chat.complaintStatus === 'OPEN' ? (
          <button type="button" className="chat-thread__side" onClick={() => void resolve()} disabled={busy}>
            Закрыть
          </button>
        ) : null}
      </header>

      {chat.kind === 'ORDER' ? (
        <p className="chat-thread__note">
          {chat.purged
            ? 'Сообщения этого заказа уже удалены.'
            : 'Переписка по заказу хранится сутки после его закрытия. Когда администратор закрывает жалобу, сообщения удаляются сразу.'}
        </p>
      ) : (
        <p className="chat-thread__note">Общая поддержка. Этот чат не удаляется.</p>
      )}

      {chat.complaintStatus === 'OPEN' ? <p className="chat-thread__note">Жалоба открыта — чат виден администратору.</p> : null}
      {error ? <p className="chat-thread__error">{error}</p> : null}

      <div
        className="chat-log"
        ref={logRef}
        onScroll={() => {
          const node = logRef.current;
          if (!node) return;
          stickRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
        }}
      >
        {olderCursor ? (
          <button type="button" className="chat-older" onClick={() => void loadOlder()}>
            Раньше
          </button>
        ) : null}
        {messages.map((message) => (
          <article
            key={message.id}
            className={`bubble${message.kind === 'SYSTEM' ? ' bubble--system' : message.mine ? ' bubble--mine' : ''}`}
          >
            {message.text ? <p className="bubble__text selectable">{message.text}</p> : null}
            {message.attachments.map((fileItem) => (
              <Attachment key={fileItem.id} id={fileItem.id} name={fileItem.name} mimeType={fileItem.mimeType} />
            ))}
            {message.kind !== 'SYSTEM' ? (
              <time className="bubble__time" dateTime={message.createdAt}>
                {new Date(message.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </time>
            ) : null}
          </article>
        ))}
      </div>

      {complaintOpen ? (
        <div className="chat-complaint">
          <textarea
            value={reason}
            maxLength={500}
            placeholder="Что пошло не так"
            onChange={(event) => setReason(event.target.value)}
          />
          <button type="button" className="composer__send" style={{ width: 'auto', padding: '0 14px' }} disabled={busy || reason.trim().length < 10} onClick={() => void complain()}>
            Отправить жалобу
          </button>
        </div>
      ) : null}

      {file ? <p className="composer__picked">Файл: {file.name}</p> : null}

      {chat.purged ? null : (
        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <input
            ref={fileRef}
            type="file"
            hidden
            accept="image/*,.pdf,.zip,.txt,application/pdf,application/zip,text/plain"
            onChange={(event) => {
              void pickFile(event.target.files);
              event.target.value = '';
            }}
          />
          <button type="button" className="composer__file" aria-label="Прикрепить файл" onClick={() => fileRef.current?.click()} disabled={busy}>
            +
          </button>
          <textarea
            rows={1}
            value={text}
            maxLength={4000}
            placeholder="Сообщение"
            className="selectable"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <button type="submit" className="composer__send" disabled={busy || (!text.trim() && !file)} aria-label="Отправить">
            ↑
          </button>
        </form>
      )}
    </div>
  );
}

function Attachment({ id, name, mimeType }: { id: string; name: string; mimeType: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!mimeType.startsWith('image/')) return undefined;

    let objectUrl = '';
    let cancel = false;

    chatApi
      .file(id)
      .then((blob) => {
        if (cancel) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);

    return () => {
      cancel = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, mimeType]);

  if (url) {
    return <img className="bubble__image" src={url} alt={name} />;
  }

  return (
    <button
      type="button"
      className="bubble__file"
      onClick={() => {
        void chatApi.file(id).then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = name;
          link.click();
          URL.revokeObjectURL(objectUrl);
        });
      }}
    >
      {name}
    </button>
  );
}
